
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Edit } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import ReactMarkdown from "react-markdown";
import { cleanMarkdownForDisplay } from "@/services/documentTransferService";

interface EditButtonProps {
  messageId: string;
  currentContent: string;
  onContentUpdated: (newContent: string) => void;
}

export const EditButton = ({ messageId, currentContent, onContentUpdated }: EditButtonProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(cleanMarkdownForDisplay(currentContent));
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();

  const handleSave = async () => {
    if (!user || !editedContent.trim()) {
      toast({
        title: "Erro",
        description: "O conteúdo não pode estar vazio.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    
    try {
      const { error } = await supabase
        .from('messages')
        .update({
          content: editedContent.trim(),
          edited: true,
          edited_at: new Date().toISOString()
        })
        .eq('id', messageId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Erro ao atualizar mensagem:', error);
        toast({
          title: "Erro ao salvar",
          description: "Não foi possível salvar as alterações. Tente novamente.",
          variant: "destructive",
        });
        return;
      }

      onContentUpdated(editedContent.trim());
      setIsEditing(false);
      
      toast({
        title: "Mensagem editada!",
        description: "As alterações foram salvas com sucesso.",
      });
      
    } catch (error) {
      console.error('Erro ao salvar edição:', error);
      toast({
        title: "Erro",
        description: "Falha ao salvar as alterações.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedContent(currentContent);
    setIsEditing(false);
  };

  return (
    <Tooltip>
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogTrigger asChild>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-5 w-5 md:h-6 md:w-6"
              aria-label="Editar resposta"
            >
              <Edit className="h-3 w-3 md:h-4 md:w-4 text-secondary-foreground/70" />
            </Button>
          </TooltipTrigger>
        </DialogTrigger>
        <DialogContent className="max-w-7xl h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Editar Resposta da IA</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-0 pb-20 overflow-hidden">
            <div className="flex flex-col min-h-0">
              <label className="text-sm font-medium mb-2">Conteúdo</label>
              <Textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="flex-1 h-[400px] resize-none"
                placeholder="Edite o conteúdo da mensagem..."
              />
            </div>
            
            <div className="flex flex-col min-h-0">
              <label className="text-sm font-medium mb-2">Preview</label>
              <div className="flex-1 h-[400px] border rounded-md p-4 overflow-auto bg-secondary/20">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown>
                    {editedContent || "*Preview aparecerá aqui...*"}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 flex justify-end gap-2 p-6 border-t bg-background">
            <Button 
              variant="outline" 
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={isSaving || !editedContent.trim()}
            >
              {isSaving ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <TooltipContent side="top">
        <p>Editar resposta da IA</p>
      </TooltipContent>
    </Tooltip>
  );
};
